import { Navbar } from './components/layout/Navbar';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { lazy, Suspense, Component, ErrorInfo, ReactNode } from 'react';
import { Footer } from './components/layout/Footer';
import { useNavigate } from 'react-router-dom';
import { Breadcrumbs } from './components/navigation/Breadcrumbs';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { useBiases } from './hooks/useApi';
import { useEffect } from 'react';
import { GoogleTagManager } from './components/analytics/GoogleTagManager';
import { RouteTracker } from './components/analytics/RouteTracker';
import { HomePageSkeleton, BiasPageSkeleton, GenericPageSkeleton, FooterSkeleton } from './components/skeletons';
import Blog from './components/blog/Blog';
import BlogPost from './components/blog/BlogPost';
import { ScrollToTopOnNavigation } from './components/layout/ScrollToTopOnNavigation';
import { CookieConsent } from './components/common/CookieConsent';
import { ConsentService } from './services/ConsentService';

// Lazy load pages with route-based code splitting
const HomePage = lazy(() => import('./pages/HomePage').then(module => ({ default: module.HomePage })));
const AboutPage = lazy(() => import('./pages/AboutPage').then(module => ({ default: module.AboutPage })));
const CategoryPage = lazy(() => import('./pages/CategoryPage').then(module => ({ default: module.CategoryPage })));
const BiasPage = lazy(() => import('./pages/BiasPage').then(module => ({ default: module.BiasPage })));

// Group practice-related pages
const PracticePages = {
  PracticePage: lazy(() => import('./pages/practice/PracticePage').then(module => ({ default: module.PracticePage }))),
  FlipcardsPage: lazy(() => import('./pages/practice/FlipcardsPage').then(module => ({ default: module.FlipcardsPage }))),
  TestPage: lazy(() => import('./pages/practice/TestPage').then(module => ({ default: module.TestPage }))),
  MatchPage: lazy(() => import('./pages/practice/MatchPage').then(module => ({ default: module.MatchPage }))),
  RolePlayDebiasPage: lazy(() => import('./pages/practice/RolePlayDebiasPage').then(module => ({ default: module.default }))),
};

// Group legal pages
const LegalPages = {
  PrivacyPage: lazy(() => import('./pages/legal/PrivacyPage').then(module => ({ default: module.PrivacyPage }))),
  TermsPage: lazy(() => import('./pages/legal/TermsPage').then(module => ({ default: module.TermsPage }))),
};

// Other pages
const LearnPage = lazy(() => import('./pages/LearnPage').then(module => ({ default: module.LearnPage })));
const ContactPage = lazy(() => import('./pages/ContactPage').then(module => ({ default: module.ContactPage })));
const BiasesListPage = lazy(() => import('./pages/BiasesListPage').then(module => ({ default: module.BiasesListPage })));
const BiasNetworkPage = lazy(() => import('./pages/BiasNetworkPage'));
const QuizPage = lazy(() => import('./pages/QuizPage'));
const SitemapPage = lazy(() => import('./pages/SitemapPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const PreMortemPage = lazy(() => import('./pages/PreMortemPage').then(module => ({ default: module.PreMortemPage })));

// Loading component that selects the appropriate skeleton based on route
const LoadingFallback = () => {
  const location = useLocation();
  
  if (location.pathname === '/') {
    return <HomePageSkeleton />;
  }
  
  if (location.pathname.startsWith('/bias/')) {
    return <BiasPageSkeleton />;
  }
  
  return <GenericPageSkeleton />;
};

// SEO configuration
const SITE_URL = 'https://www.cognitivebiaslab.com'; // Using www version as canonical

// Default meta tags component - will only render if no page-specific tags exist
const DefaultHead = () => (
  <Helmet prioritizeSeoTags defaultTitle="Cognitive Bias Lab | Interactive Learning Platform">
    <title>Cognitive Bias Lab | Interactive Learning Platform</title>
    <meta name="description" content="Learn about cognitive biases through interactive tools. Our platform offers tests, flashcards, and examples to improve your critical thinking skills." key="description" data-source="default-head" />
    <meta name="keywords" content="cognitive bias, cognitive psychology, decision making, critical thinking, bias training, psychological biases, interactive learning" key="keywords" data-source="default-head" />
    <meta property="og:type" content="website" key="og:type" data-source="default-head" />
    <meta property="og:site_name" content="Cognitive Bias Lab" key="og:site_name" data-source="default-head" />
  </Helmet>
);

// Head component for SEO - only handles non-overrideable tags
const Head = () => {
  const location = useLocation();
  const isNonWWW = window.location.hostname === 'cognitivebiaslab.com';
  const canonicalUrl = `${SITE_URL}${location.pathname}`;
  
  // Define routes that have their own specific canonical links
  const routesWithSpecificCanonicals = [
    '/biases/',
    '/contact/',
    '/learn/',
    '/practice/match/',
    '/practice/test/',
    '/practice/flipcards/',
    '/network/',
    '/quiz/',
    '/about/',
    '/privacy/',
    '/terms/',
    '/sitemap-page/',
    '/pre-mortem/',
    '/category/memory/',
    '/category/social/',
    '/category/decision-making/',
    '/category/perceptual/',
    '/category/reasoning/'
  ];
  
  // Check if current path has a specific canonical link
  const hasSpecificCanonical = routesWithSpecificCanonicals.some(route => {
    // Normalize paths by ensuring they have trailing slashes
    const normalizedPath = location.pathname.endsWith('/') 
      ? location.pathname 
      : `${location.pathname}/`;
    
    const normalizedRoute = route.endsWith('/') 
      ? route 
      : `${route}/`;
    
    return normalizedPath === normalizedRoute || 
      (normalizedRoute !== '/' && normalizedPath.startsWith(normalizedRoute));
  });
  
  // For bias pages, they have their own canonical links
  const isBiasPage = location.pathname.startsWith('/bias/');
  
  // For category pages, they have their own canonical links
  const isCategoryPage = location.pathname.startsWith('/category/');

  // Get the canonical URL for the current page
  const getCanonicalUrl = () => {
    // For homepage, use the root URL
    if (location.pathname === '/') {
      return SITE_URL;
    }
    
    // For all pages (including category, network, blog), ensure trailing slash
    const path = location.pathname.endsWith('/') 
      ? location.pathname 
      : `${location.pathname}/`;
    
    return `${SITE_URL}${path}`;
  };

  // Check if we're in prerendering mode
  const isPrerenderMode = typeof window !== 'undefined' && 
    ((window as any).__PRERENDER_INJECTED || 
     (window as any).__PRERENDERED || 
     navigator.userAgent.indexOf('Prerender') !== -1 ||
     document.querySelector('#prerender-status[data-status="ready"]') !== null);

  // Log the canonical URL for debugging
  useEffect(() => {
    console.log('[Debug] Head - Canonical URL:', getCanonicalUrl());
    console.log('[Debug] Head - Is bias page:', isBiasPage);
    console.log('[Debug] Head - Is category page:', isCategoryPage);
    console.log('[Debug] Head - Has specific canonical:', hasSpecificCanonical);
  }, [location.pathname]);

  return (
    <Helmet prioritizeSeoTags>
      {/* Always add canonical link for routes without specific component-level canonicals */}
      {!isBiasPage && !isCategoryPage && !hasSpecificCanonical && (
        <link rel="canonical" href={getCanonicalUrl()} key="canonical" data-source="page-component" />
      )}
      
      {/* Redirect non-www to www */}
      {isNonWWW && (
        <script>{`
          if (window.location.hostname === 'cognitivebiaslab.com') {
            window.location.href = '${canonicalUrl}';
          }
        `}</script>
      )}
      
      {/* Non-overrideable meta tags */}
      <meta name="author" content="Vladimir Loginov" data-source="central-head" />
      <meta name="robots" content="index, follow" data-source="central-head" />
      
      {/* Explicitly set the title even in central Head */}
      <title key="default-title">Cognitive Bias Lab | Interactive Learning Platform</title>
      <meta property="og:site_name" content="Cognitive Bias Lab" key="og:site_name" data-source="central-head" />
      
      {/* Base image tags - can be overridden by pages */}
      <meta itemProp="image" content={`${SITE_URL}/logo.webp`} key="image" data-source="central-head" />
      <meta property="og:image" content={`${SITE_URL}/logo.webp`} key="og:image" data-source="central-head" />
      <meta property="og:image:width" content="512" key="og:image:width" data-source="central-head" />
      <meta property="og:image:height" content="512" key="og:image:height" data-source="central-head" />
      <meta property="og:image:type" content="image/webp" key="og:image:type" data-source="central-head" />
      <meta property="og:url" content={getCanonicalUrl()} key="og:url" data-source="central-head" />
      
      {/* Structured Data - WebSite */}
      <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Cognitive Bias Lab",
          "url": "${SITE_URL}",
          "description": "Interactive platform for learning about cognitive biases",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "${SITE_URL}/?search={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        }
      `}</script>
      
      {/* Structured Data - Organization (helps search engines identify site as a brand) */}
      <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Cognitive Bias Lab",
          "url": "${SITE_URL}",
          "logo": "${SITE_URL}/logo.webp",
          "sameAs": [],
          "description": "Interactive platform for learning about cognitive biases"
        }
      `}</script>
    </Helmet>
  );
};

// Error boundary component to catch JavaScript errors
class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean, error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by error boundary:', error, errorInfo);
    
    // Log error to console in a format that's easy to find in logs
    console.error('==================== ERROR BOUNDARY CAUGHT ERROR ====================');
    console.error(error.message);
    console.error(error.stack);
    console.error('====================================================================');
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md m-4">
          <h2 className="text-red-700 text-lg font-semibold mb-2">Something went wrong</h2>
          <p className="text-red-600 mb-4">
            An error occurred while rendering this page. Please try refreshing the page.
          </p>
          <pre className="bg-white p-2 rounded text-sm overflow-auto max-h-40 text-red-500">
            {this.state.error?.message}
          </pre>
          <button 
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Main component
function CognitiveBiasApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useBiases();
  
  // Extract all biases from the infinite query pages
  const biases = data?.pages?.flatMap(page => page.biases) ?? [];
  
  // Check if we're in prerendering mode
  const isPrerenderMode = typeof window !== 'undefined' && 
    ((window as any).__PRERENDER_INJECTED || 
     (window as any).__PRERENDERED || 
     navigator.userAgent.indexOf('Prerender') !== -1 ||
     document.querySelector('#prerender-status[data-status="ready"]') !== null);
  
  // Add debugging for prerendering and canonical tags
  useEffect(() => {
    console.log('[Debug] CognitiveBiasApp - Prerendering detected:', isPrerenderMode);
    
    // Debug the canonical tags after component mounts
    if (typeof document !== 'undefined') {
      setTimeout(() => {
        const canonicals = document.querySelectorAll('link[rel="canonical"]');
        console.log('[Debug] CognitiveBiasApp - Canonical tags count:', canonicals.length);
        canonicals.forEach((tag, index) => {
          console.log(`[Debug] Canonical #${index + 1}:`, {
            href: tag.getAttribute('href'),
            source: tag.getAttribute('data-source')
          });
        });
      }, 1000); // Wait for potential React Helmet updates
    }
  }, [location.pathname, isPrerenderMode]);
  
  // Fetch all pages when component mounts
  useEffect(() => {
    const fetchAllPages = async () => {
      let pageCount = 0;
      const maxPages = 10; // Safety limit
      
      while (hasNextPage && pageCount < maxPages && !isLoading) {
        await fetchNextPage();
        pageCount++;
      }
      
      // Signal that the app is fully rendered
      if (isPrerenderMode) {
        document.dispatchEvent(new Event('app-rendered'));
      }
    };
    
    if (!isLoading && hasNextPage) {
      fetchAllPages();
    } else if (!isLoading && !hasNextPage) {
      // If we don't have more pages to fetch, signal that the app is rendered
      if (isPrerenderMode) {
        document.dispatchEvent(new Event('app-rendered'));
      }
    }
  }, [hasNextPage, fetchNextPage, isLoading, isPrerenderMode]);

  // Create a function to check if the page is ready for prerendering
  const checkPageReady = () => {
    const h1Elements = document.querySelectorAll('h1');
    const metaDescription = document.querySelector('meta[name="description"]');
    const canonical = document.querySelector('link[rel="canonical"]');
    
    // For bias pages, also check if bias content is loaded
    const isBiasPage = location.pathname.startsWith('/bias/');
    const biasContent = isBiasPage ? document.querySelector('.bias-page') : null;
    
    // Check for multiple canonical tags
    const canonicals = document.querySelectorAll('link[rel="canonical"]');
    if (canonicals.length > 1) {
      // Keep only the most specific canonical tag (the one with data-source="page-component")
      let keepCanonical: HTMLLinkElement | null = null;
      let keepPriority = -1;
      
      canonicals.forEach((tag: Element) => {
        const linkTag = tag as HTMLLinkElement;
        const source = linkTag.getAttribute('data-source') || '';
        let priority = 0;
        
        if (source === 'page-component') priority = 3;
        else if (source === 'central-head-prerender') priority = 2;
        else if (source === 'post-process') priority = 1;
        else if (source === 'fallback') priority = 0;
        
        if (priority > keepPriority) {
          keepCanonical = linkTag;
          keepPriority = priority;
        }
      });
      
      // Remove all other canonical tags
      canonicals.forEach((tag: Element) => {
        if (tag !== keepCanonical) {
          tag.remove();
          console.log('[Debug] Removed duplicate canonical tag:', tag.getAttribute('href'));
        }
      });
    }
    
    // Check for multiple meta description tags
    const metaDescriptions = document.querySelectorAll('meta[name="description"]');
    if (metaDescriptions.length > 1) {
      // Keep only the most specific meta description tag (prioritize those with key="description")
      let keepMetaDesc: HTMLMetaElement | null = null;
      let keepPriority = -1;
      
      metaDescriptions.forEach((tag: Element) => {
        const metaTag = tag as HTMLMetaElement;
        const key = metaTag.getAttribute('key') || '';
        let priority = 0;
        
        if (key === 'description') priority = 2;
        else if (metaTag.hasAttribute('data-source')) priority = 1;
        else priority = 0;
        
        if (priority > keepPriority) {
          keepMetaDesc = metaTag;
          keepPriority = priority;
        }
      });
      
      // Remove all other meta description tags
      metaDescriptions.forEach((tag: Element) => {
        if (tag !== keepMetaDesc) {
          tag.remove();
        }
      });
    }
    
    // Also check for duplicate og:description tags
    const ogDescriptions = document.querySelectorAll('meta[property="og:description"]');
    if (ogDescriptions.length > 1) {
      // Keep only the most specific og:description tag
      let keepOgDesc: HTMLMetaElement | null = null;
      let keepPriority = -1;
      
      ogDescriptions.forEach((tag: Element) => {
        const metaTag = tag as HTMLMetaElement;
        const key = metaTag.getAttribute('key') || '';
        let priority = 0;
        
        if (key === 'og:description') priority = 2;
        else if (metaTag.hasAttribute('data-source')) priority = 1;
        else priority = 0;
        
        if (priority > keepPriority) {
          keepOgDesc = metaTag;
          keepPriority = priority;
        }
      });
      
      // Remove all other og:description tags
      ogDescriptions.forEach((tag: Element) => {
        if (tag !== keepOgDesc) {
          tag.remove();
        }
      });
    }
    
    // For bias pages, require bias content to be loaded
    if (isBiasPage) {
      return h1Elements.length > 0 && metaDescription && canonical && biasContent;
    }
    
    return h1Elements.length > 0 && metaDescription && canonical;
  };

  // Improved useEffect to handle pre-rendering
  useEffect(() => {
    // Update the prerender status element
    const updatePrerenderStatus = (status: string) => {
      const statusElement = document.getElementById('prerender-status');
      if (statusElement) {
        statusElement.setAttribute('data-status', status);
        console.log('[Debug] Prerender status updated:', status);
      } else {
        console.warn('[Debug] Prerender status element not found!');
      }
    };
    
    // Create and add the app-ready-indicator element
    const createReadyIndicator = () => {
      if (!document.getElementById('app-ready-indicator')) {
        const indicator = document.createElement('div');
        indicator.id = 'app-ready-indicator';
        indicator.style.display = 'none';
        indicator.setAttribute('data-ready', 'true');
        document.body.appendChild(indicator);
      }
    };
    
    // Add a marker for prerendering mode
    if (isPrerenderMode) {
      // Add a meta tag to indicate prerendering mode
      const prerenderedMeta = document.createElement('meta');
      prerenderedMeta.setAttribute('name', 'prerender-mode');
      prerenderedMeta.setAttribute('content', 'true');
      document.head.appendChild(prerenderedMeta);
      
      // Force data loading for bias pages
      if (location.pathname.startsWith('/bias/')) {
        const biasId = location.pathname.split('/bias/')[1]?.replace(/\//g, '');
        if (biasId) {
          // Dispatch a custom event to notify that we need to load bias data
          document.dispatchEvent(new CustomEvent('force-bias-data-load', {
            detail: { biasId }
          }));
        }
      }
    }
    
    // Set up an interval to check page readiness
    const interval = setInterval(() => {
      if (checkPageReady()) {
        // Update prerender status
        updatePrerenderStatus('ready');
        
        // Add the ready indicator to the DOM
        createReadyIndicator();
        
        // Dispatch the app-rendered event
        document.dispatchEvent(new Event('app-rendered'));
        
        clearInterval(interval);
        
        // Add marker for prerendering
        if (isPrerenderMode) {
          const marker = document.createElement('meta');
          marker.setAttribute('name', 'prerender-status');
          marker.setAttribute('content', 'rendered');
          document.head.appendChild(marker);
        }
      }
    }, 300); // Check more frequently
    
    // Safety timeout after 10 seconds
    const timeout = setTimeout(() => {
      if (!checkPageReady()) {
        // Update prerender status
        updatePrerenderStatus('timeout');
        
        // Add the ready indicator to the DOM even if not fully ready
        createReadyIndicator();
        
        // Dispatch the app-rendered event
        document.dispatchEvent(new Event('app-rendered'));
        
        clearInterval(interval);
        
        // Add marker for prerendering even if not fully ready
        if (isPrerenderMode) {
          const marker = document.createElement('meta');
          marker.setAttribute('name', 'prerender-status');
          marker.setAttribute('content', 'forced');
          document.head.appendChild(marker);
        }
      }
    }, 10000);
    
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  const handleAcceptCookies = () => {
    // Update the dataLayer with the user's consent choice
    // Specify 'banner' as the source
    ConsentService.acceptCookies('banner');
    
    // You can add additional tracking here if needed
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'cookie_consent_action',
        'cookie_consent_action': 'accept'
      });
    }
  };

  const handleRejectCookies = () => {
    // Update the dataLayer with the user's consent choice
    // Specify 'banner' as the source
    ConsentService.rejectCookies('banner');
    
    // You can add additional tracking here if needed
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'cookie_consent_action',
        'cookie_consent_action': 'reject'
      });
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-orange-50">
      <Head />
      <DefaultHead />
      {/* GTM container handles both GTM and consent initialization */}
      <GoogleTagManager />
      <RouteTracker />
      <ScrollToTopOnNavigation smooth={true} delay={10} />
      <Navbar biases={biases} />
      {location.pathname !== '/' && <Breadcrumbs biases={biases} />}
      
      {/* Main content area with flex-grow to push footer to bottom */}
      <main className="flex-grow" style={{ contain: 'layout paint' }}>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={
              <HomePage biases={biases} />
            } />
            <Route path="/learn" element={
              <>
                <LearnPage biases={biases} />
                {(isLoading || isFetchingNextPage) && (
                  <div className="fixed inset-0 bg-orange-50/80 backdrop-blur-sm z-50">
                    <LoadingFallback />
                  </div>
                )}
              </>
            } />
            <Route path="/learn/" element={
              <>
                <LearnPage biases={biases} />
                {(isLoading || isFetchingNextPage) && (
                  <div className="fixed inset-0 bg-orange-50/80 backdrop-blur-sm z-50">
                    <LoadingFallback />
                  </div>
                )}
              </>
            } />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/about/" element={<AboutPage />} />
            <Route path="/category/:categoryId" element={
              <>
                <CategoryPage biases={biases} />
                {(isLoading || isFetchingNextPage) && (
                  <div className="fixed inset-0 bg-orange-50/80 backdrop-blur-sm z-50">
                    <LoadingFallback />
                  </div>
                )}
              </>
            } />
            <Route path="/bias/:biasId" element={
              <>
                <BiasPage biases={biases} />
                {(isLoading || isFetchingNextPage) && (
                  <div className="fixed inset-0 bg-orange-50/80 backdrop-blur-sm z-50">
                    <LoadingFallback />
                  </div>
                )}
              </>
            } />
            <Route path="/practice" element={<PracticePages.PracticePage />} />
            <Route path="/practice/test" element={
              isLoading || isFetchingNextPage ? <LoadingFallback /> : <PracticePages.TestPage biases={biases} />
            } />
            <Route path="/practice/flipcards" element={
              isLoading || isFetchingNextPage ? <LoadingFallback /> : <PracticePages.FlipcardsPage biases={biases} />
            } />
            <Route path="/practice/match" element={
              isLoading || isFetchingNextPage ? <LoadingFallback /> : <PracticePages.MatchPage biases={biases} />
            } />
            <Route path="/practice/role-play-debias" element={<PracticePages.RolePlayDebiasPage />} />
            <Route path="/privacy" element={<LegalPages.PrivacyPage />} />
            <Route path="/privacy/" element={<LegalPages.PrivacyPage />} />
            <Route path="/terms" element={<LegalPages.TermsPage />} />
            <Route path="/terms/" element={<LegalPages.TermsPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/contact/" element={<ContactPage />} />
            <Route path="/biases" element={
              isLoading || isFetchingNextPage ? <LoadingFallback /> : <BiasesListPage biases={biases} />
            } />
            <Route path="/biases/" element={
              isLoading || isFetchingNextPage ? <LoadingFallback /> : <BiasesListPage biases={biases} />
            } />
            <Route path="/network" element={
              isLoading || isFetchingNextPage ? <LoadingFallback /> : <BiasNetworkPage />
            } />
            <Route path="/network/" element={
              isLoading || isFetchingNextPage ? <LoadingFallback /> : <BiasNetworkPage />
            } />
            <Route path="/quiz" element={<QuizPage />} />
            <Route path="/quiz/" element={<QuizPage />} />
            <Route path="/sitemap-page" element={
              isLoading || isFetchingNextPage ? <LoadingFallback /> : <SitemapPage biases={biases} />
            } />
            <Route path="/sitemap-page/" element={
              isLoading || isFetchingNextPage ? <LoadingFallback /> : <SitemapPage biases={biases} />
            } />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/support/" element={<SupportPage />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/blog/:slug/" element={<BlogPost />} />
            <Route path="/pre-mortem" element={
              isLoading || isFetchingNextPage ? <LoadingFallback /> : <PreMortemPage biases={biases} />
            } />
            <Route path="/pre-mortem/" element={
              isLoading || isFetchingNextPage ? <LoadingFallback /> : <PreMortemPage biases={biases} />
            } />
          </Routes>
        </Suspense>
      </main>
      
      {/* Footer with fixed height to prevent layout shifts */}
      <div className="footer-container" style={{ contain: 'content', willChange: 'auto', overflow: 'visible' }}>
        {isLoading || isFetchingNextPage ? (
          <FooterSkeleton />
        ) : (
          <Footer setCurrentPage={(page) => navigate(`/${page}`)} />
        )}
      </div>
      
      {/* Cookie Consent Banner - this will be shown based on user's consent status */}
      <CookieConsent 
        onAccept={handleAcceptCookies}
        onReject={handleRejectCookies}
      />
    </div>
  );
}

// Root component with providers
export default function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <Router>
          <CognitiveBiasApp />
        </Router>
      </HelmetProvider>
    </ErrorBoundary>
  );
}